import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/api/queries/me";
import {
  useDepartments,
  useDepartmentOverview,
} from "@/api/queries/departments";
import { useAppStore } from "@/store/app-store";
import { useAuth } from "@/auth/AuthProvider";
import { isRespaiAdmin } from "@/lib/permissions";
import { formatDateTime, formatInteger } from "@/lib/format";

/**
 * Drill-down reached by clicking the "Peak gas exposures" KPI on the overview.
 * Lists every incident whose peak readings crossed a NIOSH screening limit — the
 * exact incidents that add up to the KPI number, most contributors first — and
 * each row opens its incident detail so a chief can see the call behind the
 * count. These are peak-vs-limit screens (CO/H₂S/VOC), not confirmed dose
 * exceedances. Reuses the overview query (no extra fetch); see src/lib/carcinogen.ts
 * for the limit definitions and src/lib/peak-gas-incidents.ts for the ranking.
 */
export function ExposureIncidentsPage() {
  const { identity } = useAuth();
  const me = useCurrentUser();
  const isPlatformAdmin = isRespaiAdmin(identity?.role);
  const selectedDeptId = useAppStore((s) => s.selectedDepartmentId);
  const { data: departments } = useDepartments();
  const selectedDept = departments?.find((d) => d.id === selectedDeptId);
  const deptId = selectedDeptId ?? me.data?.department?.id;
  const tz = selectedDept?.timezone ?? me.data?.department?.timezone;
  const deptName = isPlatformAdmin
    ? selectedDept?.name ??
      (selectedDeptId === "all" ? "All departments" : "Select a department")
    : me.data?.department?.name ?? "Loading department…";
  const overview = useDepartmentOverview(deptId);
  const incidents = overview.data?.kpis.peak_gas_incidents ?? [];
  const total = overview.data?.kpis.peak_gas_exposures ?? 0;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Link
          to="/overview"
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Department overview
        </Link>
        <h1 className="text-2xl font-semibold">
          Peak gas exposures by incident
        </h1>
        <p className="text-sm text-muted-foreground">{deptName}</p>
      </header>

      {!deptId ? (
        isPlatformAdmin ? (
          <p className="text-sm text-muted-foreground">
            Select a department to view its screen-flagged exposures.
          </p>
        ) : (
          <Skeleton className="h-64" />
        )
      ) : overview.isLoading ? (
        <Skeleton className="h-64" />
      ) : overview.isError ? (
        <p className="text-sm text-destructive">
          Failed to load exposure data.
        </p>
      ) : overview.data ? (
        incidents.length > 0 ? (
          <>
            <p className="text-sm text-muted-foreground">
              {formatInteger(total)} peak readings crossed a NIOSH screening
              limit across {formatInteger(incidents.length)}{" "}
              {incidents.length === 1 ? "incident" : "incidents"}. A crossed
              screen flags the call for review. Confirm against each incident's
              measured dose. Open an incident to see its exposure detail.
            </p>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Incidents flagged by the gas screen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-border">
                  {incidents.map((incident) => (
                    <li key={incident.incident_id}>
                      <Link
                        to={`/incidents/${incident.incident_id}`}
                        className="-mx-2 flex items-center justify-between gap-4 rounded px-2 py-3 text-sm transition-colors hover:bg-muted/50"
                      >
                        <div className="min-w-0">
                          <div className="truncate font-medium text-foreground">
                            {incident.label}
                          </div>
                          {incident.dispatched_at ? (
                            <div className="text-xs text-muted-foreground">
                              {formatDateTime(incident.dispatched_at, tz)}
                            </div>
                          ) : null}
                        </div>
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          {formatInteger(incident.count)}{" "}
                          {incident.count === 1 ? "reading" : "readings"} over
                          screening limit
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            No readings crossed a NIOSH screening limit in this window.
          </p>
        )
      ) : null}
    </div>
  );
}
